/**
 * Quiet Gallery cart page — custom qty steppers + Cart AJAX refresh.
 * Drawer qty updates remain Horizon-owned (cart-quantity-selector-component).
 */

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

function formatMoney(cents, format) {
  if (typeof cents !== 'number') cents = Number(cents) || 0;
  const value = (cents / 100).toFixed(2);
  const placeholder = format && format.includes('{{amount_no_decimals}}')
    ? String(Math.round(cents / 100))
    : value;
  if (!format) return `£${value}`;
  return format
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, value.replace('.', ','))
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, placeholder)
    .replace(/\{\{\s*amount\s*\}\}/g, value);
}

function itemLabel(count) {
  return `${count} ${count === 1 ? 'item' : 'items'}`;
}

async function changeLine({ key, line, quantity }) {
  const body = key
    ? { id: key, quantity }
    : { line, quantity };

  const endpoint = `${ROOT.replace(/\/$/, '')}/cart/change.js`;
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.description || err.message || 'Could not update cart');
  }

  return response.json();
}

function applyCart(form, cart) {
  const moneyFormat = form.dataset.moneyFormat || '{{amount}}';
  const moneyWithCurrency = form.dataset.moneyWithCurrencyFormat || moneyFormat;

  for (const row of form.querySelectorAll('[data-tpx-line-key]')) {
    const key = row.getAttribute('data-tpx-line-key');
    const item = cart.items.find((entry) => entry.key === key);

    if (!item || item.quantity <= 0) {
      row.remove();
      continue;
    }

    const input = row.querySelector('[data-tpx-qty-input]');
    if (input && Number(input.value) !== item.quantity) {
      input.value = String(item.quantity);
    }

    const priceRow = row.querySelector('[data-tpx-line-price]');
    const finalEl = priceRow && priceRow.querySelector('[data-tpx-line-final]');
    if (finalEl) finalEl.textContent = formatMoney(item.final_line_price, moneyFormat);

    const compareEl = priceRow && priceRow.querySelector('[data-tpx-line-compare]');
    if (compareEl) {
      if (item.original_line_price !== item.final_line_price) {
        compareEl.hidden = false;
        compareEl.textContent = formatMoney(item.original_line_price, moneyFormat);
      } else {
        compareEl.hidden = true;
      }
    }
  }

  const subtotal = form.querySelector('[data-tpx-cart-subtotal]');
  if (subtotal) subtotal.textContent = formatMoney(cart.items_subtotal_price, moneyFormat);

  const total = form.querySelector('[data-tpx-cart-total]');
  if (total) total.textContent = formatMoney(cart.total_price, moneyWithCurrency);

  const original = form.querySelector('[data-tpx-cart-original]');
  if (original) original.textContent = formatMoney(cart.original_total_price, moneyFormat);

  const count = document.querySelector('[data-tpx-cart-count]');
  if (count) count.textContent = itemLabel(cart.item_count);

  document.dispatchEvent(
    new CustomEvent('cart:updated', { detail: { cart }, bubbles: true }),
  );

  if (cart.item_count === 0) {
    window.location.reload();
  }
}

function bindForm(form) {
  if (form.dataset.tpxBound === 'true') return;
  form.dataset.tpxBound = 'true';

  let pending = null;

  async function commit(input) {
    const quantity = Math.max(0, Number.parseInt(input.value, 10) || 0);
    input.value = String(quantity);

    const key = input.dataset.key;
    const line = Number(input.dataset.line);

    form.setAttribute('aria-busy', 'true');
    try {
      const cart = await changeLine({ key, line, quantity });
      applyCart(form, cart);
    } catch (error) {
      console.error('[tpx-cart]', error);
      window.location.reload();
    } finally {
      form.removeAttribute('aria-busy');
    }
  }

  function schedule(input) {
    if (pending) window.clearTimeout(pending);
    pending = window.setTimeout(() => commit(input), 280);
  }

  form.addEventListener('click', (event) => {
    const button = event.target.closest('[data-tpx-qty-dec], [data-tpx-qty-inc]');
    if (!button || !form.contains(button)) return;

    event.preventDefault();
    const control = button.closest('[data-tpx-qty]');
    const input = control && control.querySelector('[data-tpx-qty-input]');
    if (!input || input.disabled) return;

    const step = Number(input.step) || 1;
    const min = Number(input.min);
    let next = Number(input.value) || 0;
    next = button.hasAttribute('data-tpx-qty-inc') ? next + step : next - step;
    if (!Number.isNaN(min)) next = Math.max(min, next);
    input.value = String(next);
    schedule(input);
  });

  form.addEventListener('change', (event) => {
    const input = event.target.closest('[data-tpx-qty-input]');
    if (!input || !form.contains(input)) return;
    schedule(input);
  });
}

function init() {
  document.querySelectorAll('[data-tpx-cart]').forEach(bindForm);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
